import axios from 'axios';
import * as Sentry from '@sentry/react';

const REQUEST_ID_HEADER = 'x-request-id';
const MAX_RESPONSE_CHARS = 2000;
const REDACTED_VALUE = '[redacted]';

const sensitiveKeyPattern = /(email|phone|tel|name|customer|address|token|authorization|password)/i;

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
};

const toLowerCaseHeaderMap = (headers = {}) => {
  const result = {};
  Object.entries(headers).forEach(([key, value]) => {
    if (key) {
      result[key.toLowerCase()] = value;
    }
  });
  return result;
};

const pickSafeHeaders = (headers) => {
  const normalized = toLowerCaseHeaderMap(headers);
  const allowed = ['accept', 'content-type', REQUEST_ID_HEADER];
  const result = {};
  allowed.forEach((name) => {
    if (normalized[name] !== undefined) {
      result[name] = normalized[name];
    }
  });
  return result;
};

const sanitizeValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      acc[key] = sensitiveKeyPattern.test(key) ? REDACTED_VALUE : sanitizeValue(val);
      return acc;
    }, {});
  }
  return value;
};

const safeSerialize = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.slice(0, MAX_RESPONSE_CHARS);
  }
  try {
    const sanitized = sanitizeValue(value);
    const serialized = JSON.stringify(sanitized);
    return serialized.length > MAX_RESPONSE_CHARS
      ? `${serialized.slice(0, MAX_RESPONSE_CHARS)}…`
      : serialized;
  } catch (error) {
    return '[unserializable]';
  }
};

const getRequestUrl = (config) => {
  if (!config) return '';
  if (config.baseURL && config.url) {
    return `${config.baseURL.replace(/\/$/, '')}/${config.url.replace(/^\//, '')}`;
  }
  return config.url || config.baseURL || '';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.alexievsbarbershop.com';

const client = axios.create({ baseURL: API_BASE_URL });

client.interceptors.request.use((config) => {
  const requestId = generateRequestId();
  const startTime = Date.now();
  config.metadata = { startTime, requestId };
  config.headers = config.headers || {};
  config.headers[REQUEST_ID_HEADER] = requestId;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config || {};
    const requestId = config.metadata?.requestId || generateRequestId();
    const startTime = config.metadata?.startTime || Date.now();
    const duration = Date.now() - startTime;
    const status = error.response?.status;
    const responseData = safeSerialize(error.response?.data);
    const requestHeaders = pickSafeHeaders(config.headers);
    const requestUrl = getRequestUrl(config);
    const axiosCode = error.code;

    error.requestId = requestId;

    Sentry.withScope((scope) => {
      scope.setTag('request_id', requestId);
      if (status) {
        scope.setTag('http_status', String(status));
      }
      if (axiosCode) {
        scope.setTag('axios_code', axiosCode);
      }
      scope.setExtra('request_url', requestUrl);
      scope.setExtra('request_headers', requestHeaders);
      scope.setExtra('request_duration_ms', duration);
      scope.setExtra('response_data', responseData);
      scope.setExtra('response_status', status || null);
      scope.setExtra('response_status_text', error.response?.statusText || '');
      scope.captureException(error);
    });

    return Promise.reject(error);
  }
);

export default client;
